import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import moment from 'moment'

import { makeStyles } from '@mui/styles'
import lang from '@/lang'
import config from '@/config'
import documentExportConfig from '@/modules/document-export/config'
import Loader from '@/modules/base/components/loader/loader'
import { Title } from '@/modules/base/components/texts/index'
import { ActionCell, ContentCell } from './cell-renderer/index'
import { fetchDocumentExports, fetchTaxAccountantSettings } from '@/redux/ducks/documentExport/list'
import DocumentExportEmailModal from '@/modules/document-export/forms/document-export-email-modal/document-export-email-modal'
import FixErrorsDrawer from './fix-errors-drawer/fix-errors-drawer'
import store from '@/redux/store'
import { DatevExportType } from '@/enums/datev-export-type'
import SimpleAgGrid from '@/modules/base/components/simple-ag-grid/simple-ag-grid'
import { FinanceFormKeys } from '@/modules/document-export/constants'
import DatevService from '@/modules/apps/own/datev/datevService'
import { toast } from '@/modules/base/components/toast/toastConfig'
import useWebSocketEvent, { EventTypes } from '@/modules/webSocket'
import lastExportIcon from '@assets/images/icons/exportLast.svg'

const DATE_FORMAT = config.dateFormat.client

const { columns } = documentExportConfig

const DocumentExportList = ({ client }) => {
  const classes = useStyles()
  const { isLoading, documentExports, modalOpen, selectedExport, fixErrorsOpen } = useSelector(state => state.documentExport.list)

  const [transferList, setTransferList] = useState([])

  useWebSocketEvent(EventTypes.DATEV_TRANSFER_JOB_Prefix, (msg) => {
    fetchTransferList()
  })

  const combinedExports = [...documentExports, ...transferList]
  const sortedCombinedExports = combinedExports.sort((a, b) => moment(b.createdAt).diff(moment(a.createdAt)))

  const dispatch = useDispatch()

  useEffect(() => {
    if ((client && client.isAllowed) || !client) {
      dispatch(fetchDocumentExports(client?.id))
      dispatch(fetchTaxAccountantSettings(client?.id))
    }
  }, [fetchDocumentExports, fetchTaxAccountantSettings, client])

  useEffect(() => {
    fetchTransferList()
  }, [documentExports, client])

  const fetchTransferList = () => {
    if ((client && client.isAllowed) || !client) {
      DatevService.getTransferList(client?.id)
        .then(data => {
          setTransferList(data)
        })
        .catch(() => toast.showToast({ type: 'error', message: lang.accounting.getTransferListErrorMessage }),
        )
    }
  }

  const getRowHeight = ({ data }) => {
    const longText = [DatevExportType.invoices, DatevExportType.bankBookings]
    const height = 20 + data.content.reduce((result, item) => {
      return result + (longText.includes(item) ? 40 : 20)
    }, 20)
    return height
  }

  const getRightClickContextMenuItems = (params) => {
    return params.value ? ['copy'] : []
  }

  const processCellForClipboard = (params) => {
    const { value, column: { colId }, node: { data } } = params

    switch (colId) {
      case 'createdAt':
        return moment(data[colId]).format(DATE_FORMAT)

      case 'content':
        return value.map(type => lang.documentExport.content[type]).join(', ')

      default:
        return data[colId] || value
    }
  }

  const emailModal = modalOpen ? <DocumentExportEmailModal open={modalOpen} selectedExport={selectedExport} /> : null

  const content = isLoading
    ? (
      <Loader visible={isLoading} text={lang.documentExport.forms.exportList.loadingTitle} customClass={classes.loader} />
    )
    : (
      <div className={`document-export ${classes.list}`}>
        <SimpleAgGrid
          domLayout='autoHeight'
          columns={columns}
          data={sortedCombinedExports}
          getRowHeight={getRowHeight}
          localeText={{
            noRowsToShow: lang.documentExport.noRowsToShow,
            loadingOoo: lang.documentExport.loadingOoo,
          }}
          getRowStyle={(params) => {
            if (params.data.isLegacy) {
              return { backgroundColor: '#FCFCFC' } // #F9F9F9
            }
          }}
          components={{
            actionCell: (props) => <ActionCell store={store} collaborationId={client?.id} {...props} />,
            contentCell: ContentCell,
          }}
          getContextMenuItems={getRightClickContextMenuItems}
          processCellForClipboard={processCellForClipboard}
          pagination={true}
          showBorder={false}
        />
      </div>
    )

  const controls = client?.isAllowed || !client
    ? (
      <>
        {content}
        {emailModal}
        {fixErrorsOpen ? <FixErrorsDrawer /> : null}
      </>
    )
    : (<>{lang.clientDetail.widgets.notAllowed}</>)
  return (
    <div className={classes.container}>
      <Title icon={lastExportIcon}>{lang.documentExport.forms.exportList.title}</Title>
      {controls}
    </div>
  )
}

DocumentExportList.financeFormKey = FinanceFormKeys.exportList

const useStyles = makeStyles(() => ({
  container: {
    position: 'relative',
    width: '100%',
  },
  loader: {
    height: '100%',
  },
  list: {
    width: '100%',
  },
}))

export default DocumentExportList
